import React, { createContext, useContext, useMemo } from 'react';

import {
  GrammarCheckerAnalytics,
  GrammarCheckerConfig,
  GrammarCheckerErrorLogger,
  resolveAnalytics,
  resolveErrorLogger,
} from '../config';

interface GrammarCheckerContextValue {
  config: GrammarCheckerConfig;
  analytics: GrammarCheckerAnalytics;
  errorLogger: GrammarCheckerErrorLogger;
}

const GrammarCheckerContext = createContext<GrammarCheckerContextValue | null>(
  null,
);

interface GrammarCheckerProviderProps {
  config: GrammarCheckerConfig;
  children: React.ReactNode;
}

export const GrammarCheckerProvider: React.FC<GrammarCheckerProviderProps> = ({
  config,
  children,
}) => {
  const value = useMemo<GrammarCheckerContextValue>(
    () => ({
      config,
      analytics: resolveAnalytics(config),
      errorLogger: resolveErrorLogger(config),
    }),
    [config],
  );

  return (
    <GrammarCheckerContext.Provider value={value}>
      {children}
    </GrammarCheckerContext.Provider>
  );
};

export const useGrammarCheckerContext = (): GrammarCheckerContextValue => {
  const ctx = useContext(GrammarCheckerContext);
  if (!ctx) {
    throw new Error(
      'useGrammarCheckerContext must be used within a GrammarCheckerProvider',
    );
  }
  return ctx;
};

export default GrammarCheckerProvider;
